import React from 'react';
import { Button } from './ui/button';

/**
 * MapButtons - Shows Google Maps and Apple Maps buttons side by side
 * @param {string} address - The address to show on the map
 * @param {string} size - Button size: 'sm', 'default', or 'icon'
 * @param {string} variant - Button variant: 'outline', 'ghost', etc.
 * @param {string} className - Additional CSS classes
 */
export default function MapButtons({ 
  address, 
  size = 'sm', 
  variant = 'outline',
  className = '',
  showLabels = true 
}) {
  if (!address) return null;

  const encodedAddress = encodeURIComponent(address);
  
  const openGoogleMaps = () => {
    window.open(`https://maps.google.com/?q=${encodedAddress}`, '_blank');
  };

  const openAppleMaps = () => {
    window.open(`https://maps.apple.com/?q=${encodedAddress}`, '_blank');
  };

  // Compact icon-only version
  if (!showLabels) {
    return (
      <div className={`flex items-center gap-1 ${className}`}>
        <Button
          variant={variant}
          size="icon"
          onClick={openGoogleMaps}
          title="Open in Google Maps"
          className="h-8 w-8"
          data-testid="google-maps-btn"
        >
          <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
          </svg>
        </Button>
        <Button
          variant={variant}
          size="icon"
          onClick={openAppleMaps}
          title="Open in Apple Maps"
          className="h-8 w-8"
          data-testid="apple-maps-btn"
        >
          <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
          </svg>
        </Button>
      </div>
    );
  }

  // Full buttons with labels
  return (
    <div className={`flex items-center gap-2 flex-wrap ${className}`}>
      <Button
        variant={variant}
        size={size}
        onClick={openGoogleMaps}
        className="text-blue-600 border-blue-200 hover:bg-blue-50 hover:border-blue-300"
        data-testid="google-maps-btn"
      >
        <svg viewBox="0 0 24 24" className="w-4 h-4 mr-1.5" fill="currentColor">
          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
        </svg>
        Google
      </Button>
      <Button
        variant={variant}
        size={size}
        onClick={openAppleMaps}
        className="text-slate-700 border-slate-200 hover:bg-slate-50 hover:border-slate-300"
        data-testid="apple-maps-btn"
      >
        <svg viewBox="0 0 24 24" className="w-4 h-4 mr-1.5" fill="currentColor">
          <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
        </svg>
        Apple
      </Button>
    </div>
  );
}
